import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Logo } from '../components/Logo';
import { SiteHeader } from '../components/SiteHeader';
import { FilingCarousel } from '../components/FilingCarousel';
import { SEO } from '../components/SEO';

export function Landing() {
  return (
    <div className="min-h-screen scroll-smooth bg-white text-slate-900">
      <SEO
        title="Online Dormant Accounts Filing Service & Software UK | £20"
        description="Cheapest online dormant accounts filing software & service in the UK. File annual dormant company accounts direct to Companies House for £20 — FRS 102 compliant, no accountant needed."
        path="/"
      />
      <SiteHeader />
      <Hero />
      <TrustStrip />
      <PoweredByMarquee />
      <DeadlineBanner />
      <HowItWorks />
      <AuthCodeSpotlight />
      <Security />
      <Pricing />
      <Comparison />
      <Faq />
      <SeoContent />
      <FinalCta />
      <Footer />
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-50 to-white"
      />
      <div className="mx-auto grid max-w-6xl gap-12 px-6 pt-6 pb-16 lg:grid-cols-2 lg:pt-10 lg:pb-20">
        <div className="flex flex-col justify-start">
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-200">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
            Direct filing to Companies House
          </span>
          <h1 className="mt-5 text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
            File dormant accounts online in{' '}
            <span className="text-brand-600">under 5 minutes</span>
          </h1>
          <p className="mt-5 max-w-xl text-lg text-slate-600">
            The UK's cheapest online dormant accounts filing service. A simple,
            direct dormant account filing service — file your annual dormant
            company accounts direct to Companies House for £20, no accountant,
            no subscription, paid once.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/search" className="btn-primary text-base">
              File now → £20
            </Link>
            <a href="#how" className="btn-ghost text-base">
              See how it works
            </a>
          </div>
          <dl className="mt-10 grid max-w-md grid-cols-3 gap-4 text-sm">
            <Stat label="One-off fee" value="£20" />
            <Stat label="Filing time" value="<5 min" />
            <Stat label="Audit-exempt" value="FRS 102" />
          </dl>
        </div>
        <div>
          <FilingCarousel />
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-lg font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

// ─── Trust strip ──────────────────────────────────────────────────────────

function TrustStrip() {
  return (
    <section className="border-y border-slate-200 bg-slate-50">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-6 py-7 text-sm sm:grid-cols-4">
        <TrustItem icon="🏛️" title="Direct to Companies House" sub="No middleman, no portal" />
        <TrustItem icon="🇬🇧" title="UK-built & supported" sub="Real humans, no chatbots" />
        <TrustItem icon="⚡" title="Same-day acceptance" sub="Usually within hours" />
        <TrustItem icon="🔁" title="No subscription" sub="Pay once per filing" />
      </div>
    </section>
  );
}

function TrustItem({ icon, title, sub }: { icon: string; title: string; sub: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-2xl leading-none">{icon}</span>
      <div>
        <div className="font-medium text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </div>
    </div>
  );
}

// ─── Powered-by marquee ───────────────────────────────────────────────────
//
// A subtle scrolling strip of honest credibility signals. Everything here we
// can legitimately claim – no fake partner logos.

function PoweredByMarquee() {
  // Renamed from "Trusted partners" – we don't have official partners yet,
  // and labelling features as such would be dishonest. This is a quick
  // scroll of value props instead. Each item is something we can stand
  // behind today.
  const items = [
    '🏛️  Filed direct to Companies House',
    '📑  FRS 102 compliant iXBRL',
    '⚡  Same-day acceptance, usually within hours',
    '🇬🇧  Built and supported in the UK',
    '💳  Card payments processed by Stripe',
    '🔒  TLS 1.3 + AES-256 encryption',
    '🗝️  Authentication code never stored',
    '📜  GDPR compliant – EU-region data',
    '🛡️  Registered with the UK ICO',
    '🛡️  No analytics, no third-party tracking',
    '↩️  Full refund if we can’t file',
    '💷  £20 per filing – no subscription',
    '🤝  Real humans on email support',
  ];
  // Duplicate the list so the loop seams perfectly.
  const loop = [...items, ...items];
  return (
    <section className="border-b border-slate-200 bg-white py-5">
      <div className="mx-auto flex max-w-6xl items-center gap-6 px-6">
        <span className="hidden flex-shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400 md:block">
          Why choose us
        </span>
        <div className="marquee-mask relative flex-1 overflow-hidden">
          <ul className="marquee flex gap-8 whitespace-nowrap py-1 text-sm text-slate-600">
            {loop.map((it, i) => (
              <li key={i} className="flex items-center gap-2 whitespace-nowrap">
                <span>{it}</span>
                <span aria-hidden className="text-slate-300">•</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─── Deadline / penalty banner ────────────────────────────────────────────

function DeadlineBanner() {
  return (
    <section className="bg-amber-50">
      <div className="mx-auto flex max-w-6xl flex-col items-start gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="text-2xl leading-none">⚠️</span>
          <div className="text-sm">
            <div className="font-semibold text-amber-900">Late filing penalties start at £150</div>
            <div className="text-amber-800">
              Then £375 after 3 months, £750 after 6, £1,500 after 12 – and double for repeat offences.
              File today and you're done in minutes.
            </div>
          </div>
        </div>
        <Link to="/search" className="btn-primary whitespace-nowrap">
          Check my deadline →
        </Link>
      </div>
    </section>
  );
}

// ─── How it works ─────────────────────────────────────────────────────────

function HowItWorks() {
  const steps = [
    {
      n: '01',
      title: 'Search your company',
      body: 'Start typing your company name or number – we fetch your details and accounts deadline straight from Companies House.',
      icon: <IconSearch />,
      time: '30 seconds',
    },
    {
      n: '02',
      title: 'Confirm dormancy',
      body: "We pre-fill last year's balance sheet so for most filings you can just confirm. New companies enter their share capital once.",
      icon: <IconCheck />,
      time: '2 minutes',
    },
    {
      n: '03',
      title: 'Enter auth code & pay',
      body: 'Provide your 6-character Companies House authentication code and complete payment by card. We never store the code – more on this below.',
      icon: <IconLock />,
      time: '1 minute',
    },
    {
      n: '04',
      title: 'We file. You get a receipt.',
      body: 'Accounts are packaged into FRS 102 iXBRL and submitted directly to Companies House. Email confirmation lands in your inbox.',
      icon: <IconSend />,
      time: 'Same day',
    },
  ];
  return (
    <section id="how" className="relative mx-auto max-w-6xl px-6 py-24">
      <div className="text-center">
        <Eyebrow>How it works</Eyebrow>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
          Four steps. No paperwork.
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-slate-600">
          From searching your company to receiving the Companies House acknowledgement, the whole
          flow takes under five minutes.
        </p>
      </div>

      {/* Connecting line behind the cards – only on desktop where they sit in a row. */}
      <div className="relative mt-16">
        <div
          aria-hidden
          className="absolute left-0 right-0 top-12 hidden h-0.5 bg-gradient-to-r from-transparent via-brand-200 to-transparent lg:block"
        />
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <div key={s.n} className="group relative">
              {/* Icon disc with gradient ring */}
              <div className="relative z-10 mx-auto flex h-24 w-24 items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-brand-100 to-brand-50 ring-1 ring-brand-200 transition-transform group-hover:scale-105" />
                <div className="absolute inset-2 rounded-full bg-white ring-1 ring-slate-200" />
                <div className="relative text-brand-700">{s.icon}</div>
                <span className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-[11px] font-bold text-white shadow-sm ring-2 ring-white">
                  {s.n}
                </span>
              </div>

              <div className="mt-6 text-center">
                <h3 className="text-base font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
                <p className="mt-3 text-[11px] font-medium uppercase tracking-wide text-brand-700">
                  ⏱ {s.time}
                </p>
              </div>

              {/* Arrow between cards on desktop */}
              {i < steps.length - 1 && (
                <div
                  aria-hidden
                  className="absolute right-[-18px] top-12 hidden h-0 w-0 -translate-y-1/2 border-y-[6px] border-l-[8px] border-y-transparent border-l-brand-300 lg:block"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Inline icons (no extra library) – stroke-based, brand-coloured via currentColor.
function IconSearch() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}
function IconLock() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
function IconSend() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

// ─── Auth-code spotlight ─────────────────────────────────────────────────

function AuthCodeSpotlight() {
  return (
    <section id="authcode" className="relative overflow-hidden bg-slate-900 text-white">
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-br from-slate-900 via-slate-900 to-brand-900/40" />
      <div className="mx-auto grid max-w-6xl gap-12 px-6 py-20 lg:grid-cols-2 lg:py-24">
        <div>
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-brand-100 ring-1 ring-white/20">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-400" />
            Companies House authentication code
          </span>
          <h2 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl">
            What is the authentication code?
          </h2>
          <p className="mt-4 text-slate-300">
            Every UK limited company has a <strong className="text-white">6-character authentication code</strong>{' '}
            (letters and numbers). Companies House uses it to confirm you're authorised to file
            accounts on behalf of your company – think of it like your company's password.
          </p>
          <ul className="mt-8 space-y-5">
            <SpotlightPoint
              title="6 characters – letters and numbers"
              body="Looks something like A7B2C9. It's case-insensitive and doesn't change unless you reset it."
            />
            <SpotlightPoint
              title="Posted to your registered office"
              body="Companies House sent it by post when your company was incorporated. It also appears on confirmation letters and on the WebFiling sign-in page."
            />
            <SpotlightPoint
              title="The same code you'd use on WebFiling"
              body="If you've ever logged in to Companies House WebFiling to file your accounts or confirmation statement, you've used this code."
            />
          </ul>

          {/* "Don't have your code?" sits on the LEFT column below the
              bullets so it appears beside the letter image on desktop
              rather than below it. */}
          <div className="mt-8 rounded-xl bg-white/5 p-5 ring-1 ring-white/10">
            <div className="text-sm font-medium text-white">Don't have your code?</div>
            <p className="mt-2 text-sm text-slate-300">
              Request a new one from Companies House – it's posted to your registered office and
              typically arrives within 5 working days.
            </p>
            <p className="mt-2 text-sm text-slate-300">
              Or <strong className="text-white">message us</strong> and we'll request it on your behalf.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <Link
                to="/contact?service=authcode"
                className="inline-flex items-center gap-1 text-sm font-semibold text-brand-200 hover:text-white"
              >
                Message us →
              </Link>
              <a
                href="https://www.gov.uk/guidance/company-authentication-codes-for-online-filing"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white"
              >
                How to request a code at gov.uk →
              </a>
            </div>
          </div>
        </div>

        <div className="relative">
          <AuthCodeLetterMock />
        </div>
      </div>
    </section>
  );
}

function SpotlightPoint({ title, body }: { title: string; body: string }) {
  return (
    <li className="flex items-start gap-4">
      <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-brand-200 ring-1 ring-brand-400/30">
        ✓
      </span>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="mt-1 text-sm text-slate-300">{body}</div>
      </div>
    </li>
  );
}

/** Photo of the real Companies House authentication-code letter. The
 * image lives at `frontend/public/auth-code-letter.png` – update that
 * file to change the visible letter. */
function AuthCodeLetterMock() {
  return (
    <div className="rounded-2xl bg-white p-3 shadow-xl ring-1 ring-white/20">
      <img
        src="/auth-code-letter.png"
        alt="Companies House authentication code letter showing the 6-character code"
        className="block w-full rounded-lg"
        loading="lazy"
      />
    </div>
  );
}

// ─── Security ─────────────────────────────────────────────────────────────

function Security() {
  const items = [
    { icon: '🔐', title: 'TLS 1.3 everywhere', body: 'All traffic between you, our servers, and Companies House is encrypted in transit using modern cipher suites.' },
    { icon: '🇪🇺', title: 'EU-region database', body: 'Customer data lives in an EU-region encrypted Postgres instance with Row-Level Security enabled. We use direct database connections – not public APIs.' },
    { icon: '🧾', title: 'Card data never touches our servers', body: 'Payments go through Stripe. We never see your card number – we only get a one-time token.' },
    { icon: '📜', title: 'GDPR compliant', body: 'You can request export or deletion of your data at any time. We retain filing records only as long as legally required (6 years for HMRC).' },
    { icon: '🛡️', title: 'No third-party sharing', body: 'Your data goes to exactly two places: Companies House (the filing) and Stripe (your payment). Nowhere else.' },
  ];
  return (
    <section id="security" className="mx-auto max-w-6xl px-6 py-20">
      <Eyebrow>Security</Eyebrow>
      <h2 className="mt-2 max-w-2xl text-3xl font-semibold tracking-tight">
        We're trusted with sensitive data – here's how we earn it.
      </h2>
      <p className="mt-3 max-w-2xl text-slate-600">
        Filing accounts means handing over your company's most sensitive credentials. We engineer
        the service so we hold the least possible amount of that data, for the least possible time.
      </p>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <div key={it.title} className="rounded-xl bg-white p-6 ring-1 ring-slate-200">
            <div className="text-2xl leading-none">{it.icon}</div>
            <h3 className="mt-4 font-semibold text-slate-900">{it.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{it.body}</p>
          </div>
        ))}
      </div>

      {/* ICO registration — prominent trust signal. Anyone in the UK
          handling personal data is required to register; displaying the
          number here is reassurance that we're properly registered. */}
      <div className="mt-12 flex flex-col items-center gap-6 rounded-2xl bg-white p-8 ring-1 ring-slate-200 sm:flex-row sm:items-center sm:gap-8 sm:p-10">
        <img
          src="/ico-badge.png"
          alt="Information Commissioner's Office registered"
          className="h-24 w-24 flex-shrink-0 object-contain sm:h-28 sm:w-28"
          width={112}
          height={112}
        />
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Registered with the Information Commissioner's Office
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            We're registered as a data controller with the UK ICO, the
            independent authority that upholds information rights for UK
            citizens. We handle your data in line with the UK GDPR.
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────

function Pricing() {
  const features = [
    'Filed directly to Companies House',
    'No accountant required',
    'Pre-filled balance sheet from last year',
    'Approval-quality FRS 102 iXBRL',
    'Same-day acceptance, email receipt',
    'Friendly support if anything goes wrong',
    'No subscription – pay per filing',
  ];
  return (
    <section id="pricing" className="bg-slate-50 py-20">
      <div className="mx-auto max-w-6xl px-6">
        <Eyebrow>Pricing</Eyebrow>
        <h2 className="mt-2 max-w-2xl text-3xl font-semibold tracking-tight">
          One filing. One price.
        </h2>
        <p className="mt-3 max-w-2xl text-slate-600">
          No hidden fees, no upsells, no surprise renewals. £20 per filing – that's it.
        </p>

        <div className="mt-10 grid items-start gap-6 md:grid-cols-2">
          <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-bold tracking-tight text-slate-900">£20</span>
              <span className="text-sm text-slate-500">per filing · paid once</span>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              All-inclusive price for filing one set of dormant accounts. No VAT.
            </p>
            <Link to="/search" className="btn-primary mt-6 w-full justify-center text-base">
              Start filing now
            </Link>
            <p className="mt-3 text-center text-xs text-slate-400">
              Payment by card (Stripe). See{' '}
              <Link to="/terms" className="hover:underline">Terms</Link>.
            </p>
          </div>

          <ul className="space-y-3 text-sm text-slate-700">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-xs text-green-700">✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─── Comparison ──────────────────────────────────────────────────────────

function Comparison() {
  const rows = [
    { label: 'Typical price', us: '£20', acc: '£100–£300', diy: 'Free' },
    { label: 'Time to file', us: 'Under 5 min', acc: '1–4 weeks', diy: '30–60 min' },
    { label: 'Pre-filled from last year', us: '✓', acc: '✓', diy: '✗' },
    { label: 'Direct to Companies House', us: '✓', acc: '✓', diy: '✓' },
    { label: 'No accountant relationship', us: '✓', acc: '✗', diy: '✓' },
    { label: 'No risk of forms wrong', us: '✓', acc: '✓', diy: '⚠ Manual entry' },
  ];
  return (
    <section id="compare" className="mx-auto max-w-6xl px-6 py-20">
      <Eyebrow>Why choose us</Eyebrow>
      <h2 className="mt-2 max-w-2xl text-3xl font-semibold tracking-tight">
        How we compare
      </h2>
      <div className="mt-10 overflow-hidden rounded-2xl ring-1 ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-5 py-4"></th>
              <th className="px-5 py-4 text-center">
                <span className="block text-xs uppercase tracking-wide text-brand-700">Us</span>
                <span className="block font-semibold">Dormant Accounts Filing</span>
              </th>
              <th className="px-5 py-4 text-center text-slate-500">
                <span className="block text-xs uppercase tracking-wide">Alternative</span>
                <span className="block font-semibold">Hire an accountant</span>
              </th>
              <th className="px-5 py-4 text-center text-slate-500">
                <span className="block text-xs uppercase tracking-wide">Alternative</span>
                <span className="block font-semibold">DIY on WebFiling</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.map((r) => (
              <tr key={r.label}>
                <td className="px-5 py-4 font-medium text-slate-900">{r.label}</td>
                <td className="px-5 py-4 text-center font-semibold text-brand-700">{r.us}</td>
                <td className="px-5 py-4 text-center text-slate-600">{r.acc}</td>
                <td className="px-5 py-4 text-center text-slate-600">{r.diy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────

function Faq() {
  const items = [
    {
      q: 'What counts as a "dormant" company?',
      a: 'A company is dormant if it had no significant accounting transactions during the financial year. Filing dormant accounts (AA02) keeps the company on the Companies House register without the cost of full accounts.',
    },
    {
      q: 'Will my filing be accepted by Companies House?',
      a: 'We submit directly to the Companies House XML Gateway using the standard FRS 102 dormant format (the same one accountants use). If a filing is ever rejected on our side, you pay nothing.',
    },
    {
      q: 'What is the Companies House authentication code?',
      a: "It's a 6-character code that Companies House sent to your registered office when the company was incorporated. You use it to confirm you're authorised to file accounts. We never store this code – it's held encrypted in memory only for the moment of submission.",
    },
    {
      q: "Can I file if I'm overdue?",
      a: "Yes. We'll file your accounts immediately – but Companies House may have already applied late-filing penalties. Filing now stops further penalties accruing.",
    },
    {
      q: 'I paid but my filing was rejected (wrong auth code, etc.) – what now?',
      a: "Don't worry. Email contact.dormantfiling@gmail.com with your order ID and our team will help you get the filing through. We'll resubmit with a corrected code once you confirm it – no need to pay again. If we genuinely can't file your accounts, we'll work it out with you.",
    },
    {
      q: 'Is there a subscription?',
      a: 'No. £20 per filing, paid once. We do send a free reminder 30 days before your next year is due, but no auto-charge.',
    },
    {
      q: 'How is my data protected?',
      a: 'TLS 1.3 to our servers, encrypted at rest in EU-region Postgres, AES-256-GCM for in-flight sensitive fields (your authentication code never touches disk), and full GDPR compliance. We never share data with third parties beyond Companies House and our payment processor.',
    },
  ];
  return (
    <section id="faq" className="bg-slate-50 py-20">
      <div className="mx-auto max-w-3xl px-6">
        <Eyebrow>FAQ</Eyebrow>
        <h2 className="mt-2 text-3xl font-semibold tracking-tight">Common questions</h2>
        <div className="mt-8 space-y-3">
          {items.map((item, i) => (
            <FaqItem key={i} q={item.q} a={item.a} />
          ))}
        </div>
        <p className="mt-10 text-center text-sm text-slate-500">
          Still have a question? Email{' '}
          <a href="mailto:contact.dormantfiling@gmail.com" className="text-brand-700 hover:underline">
            contact.dormantfiling@gmail.com
          </a>
        </p>
      </div>
    </section>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl bg-white ring-1 ring-slate-200">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="font-medium text-slate-900">{q}</span>
        <span className={`text-slate-400 transition-transform ${open ? 'rotate-45' : ''}`}>+</span>
      </button>
      {open && <p className="border-t border-slate-100 px-5 py-4 text-sm leading-relaxed text-slate-600">{a}</p>}
    </div>
  );
}

// ─── SEO content ──────────────────────────────────────────────────────────
//
// Long-form copy block that naturally uses every keyword variation we want
// to rank for ("dormant accounts filing", "dormant accounts online",
// "cheapest dormant accounts filing", "fastest dormant accounts filing",
// "dormant accounts software", "software for dormant accounts", etc.)
// without keyword-stuffing. Each heading + paragraph speaks to real
// reader questions that map to those search queries.

// SEO cards — the same content that used to live in a flat 2-column
// grid, now rendered as a horizontal 3D carousel with snap scrolling.
// Each slide is a lifted card with a gradient background, glass edge
// and layered shadows so it reads as a physical object rather than a
// flat text block.

const SEO_SLIDES: {
  icon: string;
  title: string;
  body: React.ReactNode;
  accent: string; // tailwind gradient classes for the number badge
}[] = [
  {
    icon: '📋',
    title: 'What is dormant accounts filing?',
    body: (
      <>
        Dormant accounts filing is the legal submission of annual accounts
        to Companies House for a UK limited company that hasn't traded
        during its financial year. Even a fully dormant company must file
        dormant accounts every year — miss the deadline and you face
        automatic late-filing penalties from Companies House.
      </>
    ),
    accent: 'from-indigo-500 to-indigo-700',
  },
  {
    icon: '💻',
    title: 'Why use an online dormant accounts filing service?',
    body: (
      <>
        An online dormant account filing service lets you file directly
        with Companies House without hiring an accountant. Our service
        prepares the correct FRS 102 Section 1A iXBRL document, signs it
        with your Companies House authentication code, and submits via
        the XML Gateway — all in under 5 minutes.
      </>
    ),
    accent: 'from-sky-500 to-sky-700',
  },
  {
    icon: '⚡',
    title: 'How to file dormant accounts in the UK',
    body: (
      <>
        To file dormant accounts you need your Companies House
        authentication code and the balance sheet figures for your
        dormant company. Most customers complete the entire process —
        from search to "filed" — in 3-5 minutes. We package the accounts,
        hand them to Companies House, and email you the acknowledgement
        number, usually within a few hours.
      </>
    ),
    accent: 'from-emerald-500 to-emerald-700',
  },
  {
    icon: '💷',
    title: 'Annual dormant accounts service — £20, paid once',
    body: (
      <>
        Every UK limited company needs an annual dormant accounts service
        while it's not trading. At £20 per filing we're the cheapest
        dormant account filing service in the UK. No subscription, no
        accountant retainer, no hidden fees. Companies House itself
        charges nothing to accept the filing, so £20 is the total cost.
      </>
    ),
    accent: 'from-amber-500 to-orange-600',
  },
  {
    icon: '✅',
    title: 'Who can use our dormant accounts filing service?',
    body: (
      <>
        Any UK private limited company that has been dormant for the
        entire financial period — no significant accounting transactions
        other than share issuance to subscribers or Companies House fees
        — can use our dormant account filing service to file directly
        with Companies House.
      </>
    ),
    accent: 'from-fuchsia-500 to-pink-600',
  },
  {
    icon: '🔒',
    title: 'Direct filing to Companies House, securely',
    body: (
      <>
        We're an authorised Companies House Presenter, filing through the
        official XML Gateway. Your authentication code is encrypted in
        memory, used once, and never written to disk — see our{' '}
        <Link to="/privacy" className="text-brand-700 hover:underline">
          Privacy Policy
        </Link>{' '}
        for the full data-handling detail.
      </>
    ),
    accent: 'from-slate-600 to-slate-800',
  },
];

function SeoContent() {
  // Render the card list twice — CSS marquee slides the whole strip
  // left by 50% (the width of one copy) so it loops seamlessly.
  const loop = [...SEO_SLIDES, ...SEO_SLIDES];

  return (
    <section className="relative overflow-hidden border-y border-slate-200 bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Ambient blobs for depth */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-brand-100/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-indigo-100/40 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-6 py-20">
        <h2 className="text-center text-3xl font-semibold tracking-tight sm:text-4xl">
          The UK's cheapest online dormant accounts filing service
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-center text-slate-600">
          Built for UK limited companies that need to file dormant accounts
          online — quickly, accurately, and without paying an accountant.
        </p>

        {/* Marquee — continuously slides left, pauses on hover.
            marquee-mask fades the edges so cards appear/disappear
            instead of popping. */}
        <div className="marquee-mask mt-12 overflow-hidden py-4">
          <div className="marquee-slow flex gap-6" style={{ width: 'max-content' }}>
            {loop.map((s, i) => (
              <article
                key={i}
                className="group relative flex min-h-[340px] w-[340px] flex-shrink-0 flex-col overflow-hidden rounded-2xl bg-white p-8 ring-1 ring-slate-200 transition-all duration-300 hover:-translate-y-2 hover:ring-brand-300 sm:w-[360px]"
                style={{
                  boxShadow:
                    '0 30px 60px -20px rgba(15,23,42,0.20), 0 12px 24px -12px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
                }}
              >
                {/* Top accent stripe */}
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${s.accent}`} />
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${s.accent} text-xl shadow-[0_8px_20px_-6px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.4)]`}
                >
                  <span className="drop-shadow-sm">{s.icon}</span>
                </div>

                <h3 className="mt-6 text-lg font-semibold leading-tight text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-3 whitespace-normal text-sm leading-relaxed text-slate-600">
                  {s.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ────────────────────────────────────────────────────────────

function FinalCta() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-20 text-center">
      <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
        Ready to file? It really is 5 minutes.
      </h2>
      <p className="mx-auto mt-3 max-w-xl text-slate-600">
        Start by searching for your company. No sign-up. No long forms.
      </p>
      <Link to="/search" className="btn-primary mt-7 inline-flex text-base">
        File my dormant accounts → £20
      </Link>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-slate-900 text-slate-400">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-white">
              <Logo variant="mark" className="h-8 w-8" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Dormant Accounts Filing</div>
              <div className="mt-1 text-xs">
                Direct FRS 102 dormant accounts filing for UK private limited companies.
              </div>
            </div>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link to="/services" className="hover:text-white">Services</Link>
            <a href="#how" className="hover:text-white">How it works</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
            <a href="#faq" className="hover:text-white">FAQ</a>
            <Link to="/blog" className="hover:text-white">Blog</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </nav>
        </div>
        <div className="mt-8 border-t border-slate-800 pt-6">
          <div className="flex flex-col gap-3 text-xs sm:flex-row sm:items-center sm:justify-between">
            <span>© {new Date().getFullYear()} Dormant Accounts Filing. All rights reserved.</span>
            <span>
              For UK private limited companies that have been dormant for the financial year.
            </span>
          </div>
          {/* Small ICO compliance line on every page footer. */}
          <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
            <img
              src="/ico-badge.png"
              alt="Information Commissioner's Office registered"
              className="h-10 w-10 flex-shrink-0 object-contain"
              width={40}
              height={40}
            />
            <span>
              Registered with the UK Information Commissioner's Office (ICO) as a
              data controller.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-wide text-brand-700">{children}</span>
  );
}
